<script setup lang="ts">
import type { HTMLAttributes } from 'vue'
import type { RadioGroupItemProps } from 'reka-ui'
import { reactiveOmit } from '@vueuse/core'
import { RadioGroupIndicator, RadioGroupItem, useForwardProps } from 'reka-ui'
import { cn } from '@ds/lib/utils'

/*
 * Reconciled to the canonical components/forms/Radio.jsx: a 20px ring with a
 * 1.5px border and a 10px disc, on the card surface — not shadcn's 16px circle
 * with a lucide glyph inside.
 *
 * (a11y) Unchecked edge is `border-strong` rather than the canonical
 * `--border-strong` value, for the same reason DSCheckbox is: that step
 * measures 2.64:1, below WCAG 1.4.11's 3:1 for a control boundary.
 */
const props = defineProps<RadioGroupItemProps & { class?: HTMLAttributes['class'] }>()

const delegatedProps = reactiveOmit(props, 'class')

const forwardedProps = useForwardProps(delegatedProps)
</script>

<template>
  <RadioGroupItem
    v-bind="forwardedProps"
    :class="
      cn(
        'peer size-5 shrink-0 rounded-full border-[1.5px] border-strong bg-card',
        'inline-flex items-center justify-center cursor-pointer',
        'transition-[border-color,box-shadow] duration-ds-fast ease-ds-standard',
        'data-[state=checked]:border-primary',
        'focus-visible:outline-none focus-visible:shadow-ds-focus',
        'disabled:cursor-not-allowed disabled:opacity-50',
        props.class,
      )
    "
  >
    <!-- A disc, as drawn — the canonical radio has no icon in it. -->
    <RadioGroupIndicator class="inline-flex items-center justify-center">
      <span class="size-2.5 rounded-full bg-primary" />
    </RadioGroupIndicator>
  </RadioGroupItem>
</template>
